import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { closurePeriodDatesLabel } from '@/types/types_closure';
import { parseCalendarDate, toCalendarDate } from '@/util/util_format';

interface ClosureRangePickerProps {
  /** `yyyy-mm-dd`, or '' for nothing chosen yet. */
  startDate: string;
  /** `yyyy-mm-dd`. Equal to the start for a single day off. */
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
}

/**
 * The span an absence covers, chosen as two clicks on one calendar.
 *
 * This replaced a pair of `<input type='date'>` fields, first day and last day.
 * Two fields state the shape of the data rather than the question being asked —
 * an admin holding "the artisan is away that fortnight in August" had to read
 * the fortnight off a calendar somewhere else and type both ends of it, and
 * neither field could show them that the span they were building crosses the
 * month boundary. Here they point at the fortnight.
 *
 * The second click is what closes the span, and clicking the *same* day twice is
 * how a single day off is said — the overwhelmingly common case, so it is the
 * shortest gesture rather than a mode to switch into. The first click already
 * commits that day as a one-day absence, which means an admin who wanted one day
 * and clicked away has the right thing recorded anyway; the second click on it
 * only confirms and closes the popover.
 *
 * The library's own range handling is not used (`onSelect`'s range is ignored in
 * favour of the day that was clicked): its rule is that clicking a one-day range
 * clears it, which is the opposite of what the second click means here.
 */
export function ClosureRangePicker({ startDate, endDate, onChange }: ClosureRangePickerProps) {
  const [open, setOpen] = useState(false);
  // The first day of a span still being drawn, `yyyy-mm-dd`. Null once the span
  // is closed, so the next click starts a new one rather than stretching the old.
  const [anchor, setAnchor] = useState<string | null>(null);

  const from = parseCalendarDate(startDate);
  const to = parseCalendarDate(endDate);

  function handleDayClick(day: Date | undefined) {
    if (!day) return;
    const clicked = toCalendarDate(day);

    if (anchor === null) {
      // A span of one day, until a second click says otherwise.
      setAnchor(clicked);
      onChange(clicked, clicked);
      return;
    }

    // Clicked before the anchor: the admin is naming the other end of the span,
    // not correcting themselves, so the earlier day becomes the first.
    onChange(clicked < anchor ? clicked : anchor, clicked < anchor ? anchor : clicked);
    setAnchor(null);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Every opening starts a new span. Otherwise a half-drawn one left behind
        // by a click outside — or by the dates being cleared when the absence was
        // added — would silently swallow the next first click as its end date.
        if (next) setAnchor(null);
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Dates the artisan is away'
          className='tw:flex tw:min-w-64 tw:items-center tw:justify-between tw:gap-3 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-left tw:text-sm'
        >
          <span className={startDate ? undefined : 'tw:text-muted-foreground'}>
            {startDate ? closurePeriodDatesLabel({ startDate, endDate }) : 'Choose dates'}
          </span>
          <CalendarDays className='tw:size-4 tw:shrink-0 tw:text-muted-foreground' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='tw:max-w-[min(20rem,calc(100vw-2rem))]'>
        <Calendar
          mode='range'
          selected={from ? { from, to: to ?? from } : undefined}
          defaultMonth={from}
          onSelect={(_range, day) => handleDayClick(day)}
        />
        <p className='tw:mt-2 tw:text-xs tw:text-muted-foreground'>
          {anchor === null
            ? 'Click the first day away.'
            : 'Now click the last day — or the same day again for a single day off.'}
        </p>
      </PopoverContent>
    </Popover>
  );
}
